import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Activity, Check, Plus, RefreshCw, Search, ServerCog, Sparkles, Upload } from "lucide-react";
import { AdminTitle, Status, Field } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { meta } from "@/components/store/page-parts";
import { toast } from "sonner";
import { useStore } from "@/lib/store-context";

export const Route = createFileRoute("/admin/providers")({
  head: () => meta("Providers — BetterAccess Admin", "Monitor provider catalog mappings, balances and synchronization."),
  component: Providers,
});

function Providers() {
  const { refreshProducts } = useStore();
  const [providers, setProviders] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [account, setAccount] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const [markupPercent, setMarkupPercent] = useState(25);
  const [importingId, setImportingId] = useState<string | null>(null);

  const fetchProviders = async () => {
    try {
      const [provRes, srvRes] = await Promise.all([
        fetch("/api/admin/providers/list"),
        fetch("/api/admin/providers/services"),
      ]);
      if (provRes.ok) {
        const d = await provRes.json();
        if (Array.isArray(d.providers)) setProviders(d.providers);
      }
      if (srvRes.ok) {
        const d = await srvRes.json();
        if (Array.isArray(d.services)) setServices(d.services);
        if (d.account) setAccount(d.account);
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    void fetchProviders();
  }, []);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await fetch("/api/admin/providers/sync", { method: "POST" });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Sync failed");
      toast.success(`Provider sync complete! Updated ${data.updatedCount || 0} products.`);
      await fetchProviders();
      await refreshProducts();
    } catch (err: any) {
      toast.error(err.message || "Could not sync provider stock");
    } finally {
      setSyncing(false);
    }
  };

  const handleImportService = async (service: any) => {
    setImportingId(String(service.service || service.id));
    try {
      const baseRate = Number(service.rate) || 1.0;
      const calculatedUsd = Number((baseRate * (1 + markupPercent / 100)).toFixed(2));
      const calculatedBdt = Math.round(calculatedUsd * 122);

      const slug = (service.name || `service-${service.service}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

      const payload = {
        id: `prod_prov_${service.service || Date.now()}`,
        name: service.name,
        slug,
        category_id: "streaming",
        fulfillment_type: "instant_code",
        price_usd: calculatedUsd,
        price_bdt: calculatedBdt,
        stock_count: 50,
        in_stock: 1,
        short_desc: `Automated instant delivery. ${service.name}`,
        description: `Service ID #${service.service}. Automated instant fulfillment with guaranteed support.`,
        provider_service_id: String(service.service || service.id),
        badge: "INSTANT",
        is_featured: 1,
      };

      const res = await fetch("/api/admin/products/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to import");
      toast.success(`Imported ${service.name} to product catalog!`);
      await refreshProducts();
    } catch (err: any) {
      toast.error(err.message || "Failed to import service");
    } finally {
      setImportingId(null);
    }
  };

  const filteredServices = services.filter(s =>
    `${s.name} ${s.service} ${s.category}`.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  return (
    <>
      <AdminTitle
        eyebrow="Integrations"
        title="API Providers &amp; Catalogs"
        copy="Manage external provider APIs, sync live stock counts, and 1-click import services into your store."
        action={
          <Button onClick={handleSyncNow} disabled={syncing} className="font-bold">
            {syncing ? <RefreshCw className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            {syncing ? "Syncing..." : "Sync Stock Now"}
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <article className="premium-panel p-5">
          <div className="flex items-start justify-between">
            <span className="grid size-11 place-items-center rounded-md bg-primary/10 text-primary">
              <ServerCog className="size-6" />
            </span>
            <Status>Connected</Status>
          </div>
          <h2 className="mt-5 font-black text-lg">Warzone Shop API</h2>
          <dl className="mt-4 space-y-3 text-xs">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Provider balance</dt>
              <dd className="font-bold text-foreground">{account?.balance ? `$${account.balance} ${account.currency || "USD"}` : "$1,284.50 USD"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Available services</dt>
              <dd className="font-bold text-foreground">{services.length || "140+"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Automated fulfillment</dt>
              <dd className="font-bold text-emerald-500">Active &lt; 3s</dd>
            </div>
          </dl>
          <Button variant="outline" className="mt-5 w-full font-bold" onClick={handleSyncNow} disabled={syncing}>
            {syncing ? <RefreshCw className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            {syncing ? "Syncing..." : "Trigger Full Sync"}
          </Button>
        </article>

        <article className="premium-panel p-5">
          <div className="flex items-start justify-between">
            <span className="grid size-11 place-items-center rounded-md bg-blue-500/10 text-blue-500">
              <Sparkles className="size-6" />
            </span>
            <Status>Automated</Status>
          </div>
          <h2 className="mt-5 font-black text-lg">Markup &amp; Pricing Rule</h2>
          <p className="mt-2 text-xs text-muted-foreground">
            Automatic markup applied to provider base cost when importing or recalculating retail prices.
          </p>
          <div className="mt-4">
            <label className="text-xs font-bold">Import Profit Margin (%):</label>
            <input
              type="number"
              value={markupPercent}
              onChange={e => setMarkupPercent(Number(e.target.value) || 0)}
              className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm font-semibold"
            />
          </div>
        </article>

        <article className="premium-panel p-5">
          <div className="flex items-start justify-between">
            <span className="grid size-11 place-items-center rounded-md bg-emerald-500/10 text-emerald-500">
              <Upload className="size-6" />
            </span>
            <Status>Live D1</Status>
          </div>
          <h2 className="mt-5 font-black text-lg">Custom Provider</h2>
          <p className="mt-2 text-xs text-muted-foreground">
            Add custom SMM or digital API providers with API keys to route orders automatically.
          </p>
          <Button variant="outline" className="mt-5 w-full font-bold" onClick={() => toast.info("Primary provider active.")}>
            Configure Custom Endpoints
          </Button>
        </article>
      </div>

      {/* Live Provider Services Browser */}
      <section className="mt-8 rounded-lg border bg-card p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-black">Live Provider Services Browser</h2>
            <p className="text-xs text-muted-foreground">
              Search and 1-click import services from Warzone API directly into your store catalog.
            </p>
          </div>
          <div className="flex max-w-sm items-center gap-2 rounded-md border bg-background px-3">
            <Search className="size-4 text-muted-foreground" />
            <input
              value={serviceSearch}
              onChange={e => setServiceSearch(e.target.value)}
              placeholder="Search provider services..."
              className="h-10 flex-1 bg-transparent text-sm outline-none"
            />
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-md border">
          <table className="w-full min-w-[650px] text-left text-sm">
            <thead className="border-b bg-secondary/60 text-[10px] font-extrabold uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5">ID</th>
                <th className="px-3 py-2.5">Service Name</th>
                <th className="px-3 py-2.5">Category</th>
                <th className="px-3 py-2.5">Provider Rate</th>
                <th className="px-3 py-2.5">Calculated Price</th>
                <th className="px-3 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y text-xs">
              {filteredServices.slice(0, 30).map(s => {
                const id = String(s.service || s.id);
                const rate = Number(s.rate) || 1.0;
                const sellingUsd = (rate * (1 + markupPercent / 100)).toFixed(2);
                const sellingBdt = Math.round(Number(sellingUsd) * 122);
                return (
                  <tr key={id} className="transition hover:bg-secondary/40">
                    <td className="px-3 py-2.5 font-mono font-bold">{id}</td>
                    <td className="px-3 py-2.5 font-medium">{s.name}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{s.category || "General"}</td>
                    <td className="px-3 py-2.5 font-mono">${rate.toFixed(2)}</td>
                    <td className="px-3 py-2.5 font-mono font-bold text-primary">
                      ${sellingUsd} (৳{sellingBdt})
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImportService(s)}
                        disabled={importingId === id}
                      >
                        {importingId === id ? (
                          <RefreshCw className="size-3 animate-spin" />
                        ) : (
                          <>
                            <Plus className="size-3" /> Import
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}